<template>
  <span class="action-button">
    <CButton color="secondary" size="sm" @click="openModal()" title="Log de alterações" :disabled="!chave" :name="chave">
      <fa-icon icon="clipboard-list" />
    </CButton>

    <modal :open="true" :name="modalName" height="auto" :width="unified ? 1100 : 1024" :lazy="true" :scrollable="true" :clickToClose="false">
      <CloseModal :close="closeModal" />
      <div class="modalbox">
        <CTabs variant="tabs" class="nav-tabs-boxed">
          <CTab title="Log de alterações">
            <div v-if="isFetching" class="">
              <LoadingMessage message="Carregando, aguarde" is-active align-center />
            </div>
            <div v-else-if="errorMessage" class="alert alert-danger">
              <pre>{{ errorMessage }}</pre>
            </div>

            <!-- Visualização unificada (diff por campo) -->
            <template v-else-if="unified">
              <div v-if="!result.itens || result.itens.length === 0" class="alert alert-info">
                Nenhuma alteração registrada para este registro.
              </div>
              <div v-else class="unified-log">
                <div v-for="(item, idx) in result.itens" :key="idx" class="log-entry">
                  <div class="log-entry-header">
                    <div class="log-entry-meta">
                      <span class="badge" :class="acaoBadgeClass(item.acao)">{{ item.acao }}</span>
                      <span class="log-user">
                        <fa-icon icon="user" /> {{ item.colaborador && item.colaborador.nome || '—' }}
                      </span>
                    </div>
                    <span class="log-datetime">
                      <fa-icon icon="clock" /> {{ toDateTime(item.datahora_criacao) }}
                    </span>
                  </div>
                  <UnifiedDiffTable
                    :antes="item.dados_anteriores || {}"
                    :novos="item.dados_novos || {}"
                    :labels="fieldLabels"
                    :resolver="resolver"
                    :resolvable-fields="resolvableFields"
                  />
                </div>
                <pagination
                  :data="result.paginator"
                  :limit="2"
                  @pagination-change-page="getLogs"
                />
              </div>
            </template>

            <!-- Visualização clássica (SQL de integração) -->
            <table-paginated
              v-else
              :itens="result.itens"
              :columns="columns"
              :paginator="result.paginator"
              :onPageChange="getLogs"
            />
          </CTab>
        </CTabs>
      </div>
    </modal>
  </span>
</template>

<script>

import CloseModal from "@/components/CloseModal";
import TablePaginated from "@/components/crud/TablePaginated";
import UnifiedDiffTable from "@/components/Log/UnifiedDiffTable.vue";
import {get} from "@/helpers/apiRequest";
import {toDateTime} from "@/helpers/utils";

export default {
  name: "LogAlteracaoDados",
  props: {
    tabela: { type: String, required: true },
    chave: { type: [String, Number], default: null },
    unified: { type: Boolean, default: false },
    fieldLabels: { type: Object, default: () => ({}) },
    resolver: { type: Function, default: null },
    resolvableFields: { type: Array, default: () => [] },
  },
  components: {
    CloseModal,
    TablePaginated,
    UnifiedDiffTable,
  },
  data() {
    return {
      errorMessage: null,
      isFetching: false,
      result : {},
      columns: [
        {label: 'Ação', field: 'acao', sortable: false},
        {label: 'Colaborador', field: 'colaborador', representedAs: (row) => row.colaborador.nome, sortable: false },
        {label: 'Dados anteriores', field: 'dados_anteriores', isKeyValue: true, sortable: false },
        {label: 'Dados novos', field: 'dados_novos', isKeyValue: true, sortable: false },
        {label: 'Data/hora', field: 'datahora_criacao', sortable: false, representedAs: (row) => toDateTime(row.datahora_criacao) },
      ],
    }
  },
  computed: {
    modalName() {
      return `log-alteracao-dados-${this.tabela}-${this.chave}`;
    }
  },
  methods: {
    toDateTime,
    getLogs(page = 1) {
      this.isFetching = true;
      this.errorMessage = null;
      let tabela = encodeURI(this.tabela);
      let chave  = encodeURI(this.chave);

      get(`/admin/log/alteracao-dados/?tabela=${tabela}&chave=${chave}&page=${page}`)
        .then((response) => {
          this.result = response;

          if (!this.unified) {
            this.genDiffSql();

            const temSql = this.result.itens?.some(item => item.dados_anteriores?.sql || item.dados_novos?.sql);

            if (temSql && !this.columns.find(col => col.field === 'sql_diff')) {
              this.columns.push({label: 'Diff SQL', field: 'sql_diff', isKeyValue: true, sortable: false });
            }
          }
        })
        .catch((error) => this.errorMessage = error.message)
        .finally(() => {
          this.isFetching = false;
        })
    },
    genDiffSql() {
      if (!this.result || !this.result.itens || !Array.isArray(this.result.itens)) return;

      for (const item of this.result.itens) {
        const dadosAnteriores = item.dados_anteriores || {};
        const dadosNovos = item.dados_novos || {};

        if (dadosAnteriores.sql || dadosNovos.sql) {
          this.$set(item, 'sql_diff', {
            sql_diff: {
              dados_anteriores: dadosAnteriores.sql || '',
              dados_novos: dadosNovos.sql || ''
            }
          });
        }
      }
    },
    openModal() {
      this.getLogs(1);
      this.$modal.show(this.modalName);
    },
    closeModal() {
      this.$modal.hide(this.modalName);
    },
    acaoBadgeClass(acao) {
      switch ((acao || '').toUpperCase()) {
        case 'CREATE': return 'badge-success';
        case 'DELETE': return 'badge-danger';
        case 'UPDATE': return 'badge-primary';
        default: return 'badge-secondary';
      }
    },
  }
}
</script>

<style scoped>
  .table tbody > tr > td {
    vertical-align: top;
    background-color: red
  }

  span.action-button {
    display: inline-block;
  }

  .unified-log {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .log-entry {
    border: 1px solid #e4e7eb;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
  }

  .log-entry-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    font-size: 12px;
  }

  .log-entry-meta {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .log-user {
    color: #344054;
    font-weight: 500;
  }

  .log-datetime {
    color: #667085;
  }
</style>
